import React, { useEffect, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { CheckoutModal } from '../../components/CheckoutModal';
import type { MenuLink } from './menuItems';
import type { StoreFacts } from './storeFacts';
import { SocialIcons } from './SocialIcons';
import './elementMenuDrawer.css';
import { named, useShopNames } from '../../commerce/shopNames';

// Sleek's phone menu: a panel over the whole screen from the start edge, the store's name and a close
// button at its top, one row per link, and a chevron on each link with links under it that slides its own
// panel in, with the way back at its top. It is the same dialog the cart opens in, so Escape, the focus
// trap and the frozen page behind come with it.

interface MenuDrawerProps {
  open: boolean;
  links: readonly MenuLink[];
  /** What the panel's top says: the store's name. */
  title: string;
  /** The store's social profiles, drawn at the panel's foot as Sleek's phone menu does. */
  social: StoreFacts['social'];
  onClose: () => void;
}

/** One top-level row: its link, how its panel opens, and whether the keyboard comes back to its chevron. */
type DrawerRowProps = { link: MenuLink; refocus: boolean; onOpen: () => void; onClose: () => void };

// Draws one row: the link, and the chevron that opens the links under it.
const DrawerRow: React.FC<DrawerRowProps> = ({ link, refocus, onOpen, onClose }) => {
  const chevron = useRef<HTMLButtonElement>(null);
  const names = useShopNames();
  // Coming back from a link's panel puts the keyboard on the chevron that opened it.
  useEffect(() => { if (refocus) chevron.current?.focus(); }, [refocus]);
  return (
    <li className="xe-mdrawer-row">
      {link.href ? <a href={link.href} onClick={onClose} dir="auto">{link.label}</a> : <span dir="auto">{link.label}</span>}
      {link.children.length > 0 ? (
        <button ref={chevron} type="button" aria-label={named(names.showMenu, { label: link.label })} onClick={onOpen}><ChevronRight size={20} aria-hidden="true" /></button>
      ) : null}
    </li>
  );
};

// Draws the links under one link: the way back, then each child, a child with links under it as a heading over them.
const SubPanel: React.FC<{ link: MenuLink; onBack: () => void; onClose: () => void }> = ({ link, onBack, onClose }) => {
  const back = useRef<HTMLButtonElement>(null);
  // Focus moves to the way back as the panel slides in, so a keyboard is never left behind on a hidden row.
  useEffect(() => { back.current?.focus(); }, []);
  return (
    <div className="xe-mdrawer-sub">
      <button ref={back} type="button" className="xe-mdrawer-back" onClick={onBack}><ChevronLeft size={20} aria-hidden="true" /><span dir="auto">{link.label}</span></button>
      <ul className="xe-mdrawer-list">
        {link.href ? <li className="xe-mdrawer-row"><a href={link.href} onClick={onClose}>All {link.label}</a></li> : null}
        {link.children.map((child) => (
          <li key={`${child.label}-${child.href ?? ''}`} className="xe-mdrawer-group">
            {child.href ? <a href={child.href} onClick={onClose} dir="auto">{child.label}</a> : <span dir="auto">{child.label}</span>}
            {child.children.length > 0 ? (
              <ul>{child.children.map((leaf) => <li key={`${leaf.label}-${leaf.href ?? ''}`}><a href={leaf.href} onClick={onClose} dir="auto">{leaf.label}</a></li>)}</ul>
            ) : null}
          </li>
        ))}
      </ul>
    </div>
  );
};

// Draws the phone menu over the page, the top-level rows or one link's panel of links under it.
export const MenuDrawer: React.FC<MenuDrawerProps> = ({ open, links, title, social, onClose }) => {
  const [shown, setShown] = useState<MenuLink | null>(null);
  const [left, setLeft] = useState<string | null>(null);
  const names = useShopNames();
  // Closing forgets which panel was open, so the menu always opens at its top.
  useEffect(() => { if (!open) { setShown(null); setLeft(null); } }, [open]);
  // Goes back to the top menu, remembering which row to put the keyboard on.
  const back = () => { setLeft(shown?.label ?? null); setShown(null); };
  return (
    <CheckoutModal open={open} placement="menu" title={named(names.menuOf, { title })} closeLabel={names.closeMenu} onClose={onClose}
      heading={<span className="xe-mdrawer-title" dir="auto">{title}</span>}>
      {shown ? <SubPanel key={shown.label} link={shown} onBack={back} onClose={onClose} /> : (
        <ul className="xe-mdrawer-list">
          {links.map((link) => (
            <DrawerRow key={`${link.label}-${link.href ?? ''}`} link={link} refocus={left === link.label} onOpen={() => setShown(link)} onClose={onClose} />
          ))}
        </ul>
      )}
      {social && social.length > 0 ? <SocialIcons className="xe-mdrawer-social" links={social} storeName={title} size={20} /> : null}
    </CheckoutModal>
  );
};
